'use client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Patch types written into the dirty map. Each editable element registers a
 * patch keyed by a stable id; on Save, all patches POST to /api/post/save.
 */
export type Patch =
  | { kind: 'frontmatter'; path: string; value: any }
  | { kind: 'text'; locator: TextLocator; oldValue: string; newValue: string }
  | { kind: 'attr'; locator: AttrLocator; attr: string; oldValue: string; newValue: string };

/** Text inside a body node — uniquely located by node-kind + occurrence index. */
export type TextLocator = {
  /** The block kind: 'p' | 'h2' | 'h3' | 'h4' | 'li' | 'blockquote' | 'cite' */
  block: string;
  /** Zero-based occurrence index of that kind in document order. */
  index: number;
};

/** JSX attribute on a custom block — located by element name + occurrence index. */
export type AttrLocator = {
  /** Component name: 'BlogImage' | 'Callout' | 'ImageCarousel' | … */
  element: string;
  /** Zero-based occurrence index of that element in document order. */
  index: number;
};

type EditorState = {
  editing: boolean;
  saving: boolean;
  dirty: Map<string, Patch>;
};

type EditorContextValue = EditorState & {
  setEditing: (v: boolean) => void;
  setPatch: (id: string, patch: Patch) => void;
  clearPatch: (id: string) => void;
  reset: () => void;
  save: () => Promise<void>;
  /** Slug of the post being edited, set by article page on mount. */
  slug: string | null;
  setSlug: (s: string | null) => void;
};

const EditorContext = createContext<EditorContextValue | null>(null);

export function useEditor() {
  const ctx = useContext(EditorContext);
  if (!ctx) throw new Error('useEditor() outside EditorProvider');
  return ctx;
}

export function EditorProvider({ children }: { children: React.ReactNode }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<Map<string, Patch>>(new Map());
  const [slug, setSlug] = useState<string | null>(null);

  const setPatch = useCallback((id: string, patch: Patch) => {
    setDirty((prev) => {
      const next = new Map(prev);
      next.set(id, patch);
      return next;
    });
  }, []);

  const clearPatch = useCallback((id: string) => {
    setDirty((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setDirty(new Map());
    // Force re-render of consumers so contentEditable elements snap back
    setEditing(false);
    setTimeout(() => setEditing(true), 0);
  }, []);

  const save = useCallback(async () => {
    if (!slug) return;
    if (dirty.size === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      const patches = Array.from(dirty.values());
      const res = await fetch('/api/post/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, patches }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`Save failed: ${res.status} ${err}`);
      }
      setDirty(new Map());
      setEditing(false);
      // Reload to fetch new content from disk
      window.location.reload();
    } catch (e: any) {
      alert(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [slug, dirty]);

  // Cmd+S / Ctrl+S to save
  useEffect(() => {
    if (!editing) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        save();
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (dirty.size > 0 && !confirm('Discard unsaved changes?')) return;
        reset();
        setEditing(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, save, reset, dirty.size]);

  // Warn on accidental navigation away
  useEffect(() => {
    if (dirty.size === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty.size]);

  const value: EditorContextValue = useMemo(
    () => ({ editing, saving, dirty, setEditing, setPatch, clearPatch, reset, save, slug, setSlug }),
    [editing, saving, dirty, setPatch, clearPatch, reset, save, slug]
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
}
